import React, { useCallback, useState, useEffect, useRef } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';

import { Modal } from './Modal';
import { Providers } from '@global/App/Providers';

export type ModalContent<TDate> = React.ElementType<{
  handleClose?: () => void;
  customData?: TDate;
  handleSubmit?: (params: unknown) => Promise<void>;
}>;

interface IProps<TDate> {
  content: ModalContent<TDate>;
  blockScreen?: boolean;
  customClass?: string;
  hideCloseButton?: boolean;
  customOverlayClass?: string;
}

export function useModal<TDate = unknown>({
  content: Content,
  blockScreen: _blockScreen,
  customClass,
  hideCloseButton,
  customOverlayClass = '',
}: IProps<TDate>) {
  const [isOpen, setIsOpen] = useState(false);
  const [customData, setCustomData] = useState<TDate>(null);
  const [blockScreen, setBlockScreen] = useState(_blockScreen);

  const rootElement = useRef<HTMLElement | null>(
    document.querySelector('#modal-root')
  );
  // main callbacks
  const handleClose = useCallback(() => {
    !blockScreen && setIsOpen(false);
    if (customData) setCustomData(null);
  }, [blockScreen]);

  const handleOpen = useCallback((openData?: TDate) => {
    if (openData) setCustomData(openData);
    setIsOpen(true);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (blockScreen) return prev;
      return !prev;
    });
  }, []);

  const handleBlockScreen = useCallback(() => {
    setBlockScreen(true);
  }, []);

  const handleUnblockScreen = useCallback(() => {
    setBlockScreen(false);
  }, []);

  const clearRoot = useCallback(() => {
    unmountComponentAtNode(rootElement.current);
    document?.body.classList.remove('noscroll');
  }, [rootElement]);

  const renderModal = useCallback(() => {
    const CustomModal = () => (
      <BrowserRouter>
        <Providers>
          <div
            className={`overlay ${customOverlayClass}`}
            onClick={handleClose}
          ></div>
          <Modal
            onClose={handleClose}
            blockScreen={blockScreen}
            customClass={customClass}
            isModalVisible={isOpen}
            hideCloseButton={hideCloseButton}
          >
            <Content handleClose={handleClose} customData={customData} />
          </Modal>
        </Providers>
      </BrowserRouter>
    );

    render(<CustomModal />, rootElement.current);
  }, [Content, rootElement, customOverlayClass, isOpen]);

  // use Effects
  useEffect(() => {
    isOpen ? renderModal() : clearRoot();
  }, [isOpen]);

  return {
    isOpen,
    handleClose,
    handleOpen,
    toggle,
    handleBlockScreen,
    handleUnblockScreen,
  };
}
